import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { getApiBaseUrl } from '../../config/apiConfig';
import {
  FaBoxOpen,
  FaPlus,
  FaTrash,
  FaSave,
  FaEdit,
  FaCheck,
  FaStar,
} from 'react-icons/fa';
import { FiCheckCircle, FiXCircle, FiX } from 'react-icons/fi';

const PLAN_DEFAULTS = {
  plan_id: '',
  name: '',
  tier: 'basic',
  max_workflows: 5,
  allowed_workflow_types: ['comment-reply'],
  allow_custom_prompt: false,
  price_monthly: 0,
  active: true,
};

const WORKFLOW_TYPES = ['comment-reply', 'comment-dm', 'dm'];

const TIER_STYLES = {
  basic: {
    badge: 'bg-gray-100 text-gray-700 border-gray-200',
    ring: 'border-gray-200',
    accent: 'text-gray-600',
  },
  pro: {
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    ring: 'border-indigo-300 ring-2 ring-indigo-100',
    accent: 'text-indigo-600',
  },
  enterprise: {
    badge: 'bg-purple-50 text-purple-700 border-purple-200',
    ring: 'border-purple-200',
    accent: 'text-purple-600',
  },
};

export default function SubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(PLAN_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const token = Cookies.get('fbauthToken');
  const API = getApiBaseUrl();

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/v2/automation/plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data;
      setPlans(Array.isArray(data) ? data : Object.values(data || {}));
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load plans' });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!form.name || !form.tier) {
      setMessage({ type: 'error', text: 'Name and tier are required' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/v2/automation/plans`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage({
        type: 'success',
        text: isEditing ? 'Plan updated' : 'Plan saved',
      });
      setShowForm(false);
      setIsEditing(false);
      setForm(PLAN_DEFAULTS);
      fetchPlans();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save plan' });
    }
    setSaving(false);
  };

  const handleEdit = (plan) => {
    setForm({
      ...PLAN_DEFAULTS,
      ...plan,
      allowed_workflow_types: plan.allowed_workflow_types || [],
    });
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Delete this plan?')) return;
    try {
      await axios.delete(`${API}/api/v2/automation/plans?plan_id=${planId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchPlans();
      setMessage({ type: 'success', text: 'Plan deleted' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete plan' });
    }
  };

  const toggleWorkflowType = (type) => {
    setForm((prev) => ({
      ...prev,
      allowed_workflow_types: prev.allowed_workflow_types.includes(type)
        ? prev.allowed_workflow_types.filter((t) => t !== type)
        : [...prev.allowed_workflow_types, type],
    }));
  };

  const activeCount = plans.filter((p) => p.active).length;

  return (
    <div className="lg:p-1 min-h-screen lg:mr-[2vw] bg-gray-50">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold text-indigo-800 flex items-center gap-2">
            <FaBoxOpen />
            <span>Subscription Plans</span>
          </h2>
          <p className="text-gray-600 mt-1">
            Manage automation subscription tiers, prices and feature limits.
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setIsEditing(false);
            setForm(PLAN_DEFAULTS);
          }}
          type="button"
          className="mt-3 md:mt-0 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors shadow-sm"
        >
          <FaPlus /> New Plan
        </button>
      </div>

      {/* Notice */}
      {message && (
        <div
          className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2 border ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {message.type === 'success' ? <FiCheckCircle /> : <FiXCircle />}
          <span className="font-medium">{message.text}</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="bg-indigo-50 text-indigo-600 p-2.5 rounded-lg">
            <FaBoxOpen />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Plans</p>
            <p className="text-2xl font-bold text-gray-800">{plans.length}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="bg-green-50 text-green-600 p-2.5 rounded-lg">
            <FaCheck />
          </div>
          <div>
            <p className="text-sm text-gray-500">Active Plans</p>
            <p className="text-2xl font-bold text-gray-800">{activeCount}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="bg-purple-50 text-purple-600 p-2.5 rounded-lg">
            <FaStar />
          </div>
          <div>
            <p className="text-sm text-gray-500">Tier Coverage</p>
            <p className="text-2xl font-bold text-gray-800">
              {new Set(plans.map((p) => p.tier)).size}
              <span className="text-sm font-normal text-gray-400"> / 3</span>
            </p>
          </div>
        </div>
      </div>

      {/* Plan list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FaBoxOpen />
            <span>All Plans</span>
          </h3>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <FaBoxOpen className="mx-auto text-4xl mb-3 opacity-40" />
            <p>No plans yet. Create your first subscription plan.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 p-5">
            {plans.map((plan, idx) => {
              const t = TIER_STYLES[plan.tier] || TIER_STYLES.basic;
              return (
                <div
                  key={plan.plan_id || idx}
                  className={`relative bg-white rounded-xl border p-5 transition-all hover:shadow-md ${t.ring}`}
                >
                  {plan.tier === 'pro' && (
                    <span className="absolute -top-2.5 left-5 inline-flex items-center gap-1 bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow">
                      <FaStar size={9} /> Popular
                    </span>
                  )}

                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-gray-800 text-lg leading-tight">
                        {plan.name}
                      </h3>
                      <span
                        className={`mt-1 inline-block text-[10px] px-2 py-0.5 rounded-full capitalize font-semibold border ${t.badge}`}
                      >
                        {plan.tier}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEdit(plan)}
                        type="button"
                        className="text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 p-2 rounded-lg transition-colors"
                        aria-label="Edit plan"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(plan.plan_id)}
                        type="button"
                        className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                        aria-label="Delete plan"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  <div className="my-4 flex items-baseline gap-2">
                    <span className={`text-3xl font-bold ${t.accent}`}>
                      &#8377;{plan.price_monthly}
                    </span>
                    <span className="text-sm text-gray-500 font-medium">
                      ({plan.max_workflows === 0 ? 'Unlimited Workflows' : `${plan.max_workflows} Workflows`})
                    </span>
                  </div>

                  <div className="h-px bg-gray-100 mb-4" />

                  <ul className="space-y-2 mb-4 text-sm">
                    <li className="flex items-start gap-2 text-gray-700">
                      <FaCheck
                        className={`mt-0.5 flex-shrink-0 ${t.accent}`}
                        size={12}
                      />
                      <span>
                        {plan.max_workflows === 0
                          ? 'Unlimited workflows'
                          : `${plan.max_workflows} workflows`}
                      </span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <FaCheck
                        className={`mt-0.5 flex-shrink-0 ${t.accent}`}
                        size={12}
                      />
                      <span>
                        {(plan.allowed_workflow_types || []).join(', ') || '—'}
                      </span>
                    </li>
                    {plan.allow_custom_prompt && (
                      <li className="flex items-start gap-2 text-gray-700">
                        <FaCheck
                          className={`mt-0.5 flex-shrink-0 ${t.accent}`}
                          size={12}
                        />
                        <span>Custom AI prompts</span>
                      </li>
                    )}
                  </ul>

                  <div
                    className={`text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-full border ${
                      plan.active
                        ? 'text-green-700 bg-green-50 border-green-200'
                        : 'text-gray-500 bg-gray-100 border-gray-200'
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        plan.active ? 'bg-green-500' : 'bg-gray-400'
                      }`}
                    />
                    {plan.active ? 'Active' : 'Inactive'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Plan form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800 text-lg">
                {isEditing ? 'Edit Subscription Plan' : 'New Subscription Plan'}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setIsEditing(false);
                }}
                className="text-gray-400 hover:text-gray-700 p-1 rounded"
                aria-label="Close"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block uppercase tracking-wider">
                  Plan Name
                </label>
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Pro"
                  value={form.name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, name: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block uppercase tracking-wider">
                  Tier
                </label>
                <select
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white capitalize"
                  value={form.tier}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, tier: e.target.value }))
                  }
                >
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block uppercase tracking-wider">
                  Max Workflows
                </label>
                <input
                  type="number"
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={form.max_workflows}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      max_workflows: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Set to 0 for unlimited.
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block uppercase tracking-wider">
                  Plan Price (&#8377;)
                </label>
                <input
                  type="number"
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={form.price_monthly}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      price_monthly: parseFloat(e.target.value) || 0,
                    }))
                  }
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-gray-500 mb-2 block uppercase tracking-wider">
                  Allowed Workflow Types
                </label>
                <div className="flex flex-wrap gap-2">
                  {WORKFLOW_TYPES.map((t) => {
                    const checked = form.allowed_workflow_types.includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => toggleWorkflowType(t)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors capitalize ${
                          checked
                            ? 'bg-indigo-50 text-indigo-700 border-indigo-300'
                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-indigo-200'
                        }`}
                      >
                        {checked && <FaCheck size={9} />}
                        {t.replace('-', ' ')}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="md:col-span-2 flex flex-wrap gap-6 pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.allow_custom_prompt}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        allow_custom_prompt: e.target.checked,
                      }))
                    }
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Allow custom AI prompt
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, active: e.target.checked }))
                    }
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Plan Active
                  </span>
                </label>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2 bg-gray-50/50">
              <button
                onClick={() => {
                  setShowForm(false);
                  setIsEditing(false);
                }}
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                type="button"
                className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
              >
                <FaSave />{' '}
                {saving ? 'Saving…' : isEditing ? 'Update Plan' : 'Save Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
